import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import QuizRoundedIcon from "@mui/icons-material/QuizRounded";
import StyleRoundedIcon from "@mui/icons-material/StyleRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  getCopilotChatHistoryCall,
  getCopilotCreditsCall,
  getStudyPackCall,
} from "../../redux/api/copilot.api";
import CreditsBar from "./components/CreditsBar";
import InsufficientCreditsModal from "./components/InsufficientCreditsModal";
import SummaryTab from "./studyPack/SummaryTab";
import KeyStructuresTab from "./studyPack/KeyStructuresTab";
import ImagesTab from "./studyPack/ImagesTab";
import QuizTab from "./studyPack/QuizTab";
import FlashcardsTab from "./studyPack/FlashcardsTab";
import CopilotChatPanel from "./studyPack/CopilotChatPanel";
import TopicsTab from "./studyPack/TopicsTab";

/*
  ─── Microlab Copilot — Study Pack screen ──────────────────────
  Web equivalent of the mobile StudyPackScreen. Renders a single
  uploaded material's content tree as 7 tabs:

    Summary | Key structures | Images | Quiz | Flashcards | Chat | Topics

  The Chat tab is the only writeable surface — every other tab
  is a read-only render of the JSONB study pack the backend
  generated during processing. Chat sends update the local
  study pack via the CopilotChatPanel's onStudyPackUpdate
  callback, which the Quiz/Flashcards/Topics tabs subscribe to
  via their `studyPack` prop — they re-render whenever new
  questions / cards / nodes land.
*/

const TAB_DEFS = [
  { id: "summary", label: "Summary", icon: <MenuBookRoundedIcon fontSize="small" /> },
  { id: "structures", label: "Key structures", icon: <AccountTreeRoundedIcon fontSize="small" /> },
  { id: "images", label: "Figures", icon: <ImageRoundedIcon fontSize="small" /> },
  { id: "quiz", label: "Quiz", icon: <QuizRoundedIcon fontSize="small" /> },
  { id: "flashcards", label: "Flashcards", icon: <StyleRoundedIcon fontSize="small" /> },
  { id: "chat", label: "Chat", icon: <ChatRoundedIcon fontSize="small" /> },
  { id: "topics", label: "Topics", icon: <HubRoundedIcon fontSize="small" /> },
];

export default function MicrolabCopilotStudyPack() {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const [material, setMaterial] = useState(null);
  const [studyPack, setStudyPack] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");
  const [chatMessages, setChatMessages] = useState([]);
  const [insufficient, setInsufficient] = useState(null);

  const refreshCredits = useCallback(async () => {
    const next = await getCopilotCreditsCall();
    if (next) setCredits(next);
  }, []);

  const loadStudyPack = useCallback(async () => {
    if (!materialId) return;
    setLoading(true);
    try {
      const res = await getStudyPackCall(materialId);
      if (res?.status === 200) {
        setMaterial(res.material || null);
        setStudyPack(res.studyPack || null);
      } else if (res?.status === 409) {
        // Not ready yet — bounce back to the processing screen
        // which polls until completion.
        navigate(`/microlab-copilot/processing/${materialId}`, { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [materialId, navigate]);

  const loadChatHistory = useCallback(async () => {
    if (!materialId) return;
    const res = await getCopilotChatHistoryCall(materialId);
    setChatMessages(res?.messages || []);
  }, [materialId]);

  useEffect(() => {
    loadStudyPack();
    loadChatHistory();
    refreshCredits();
  }, [loadStudyPack, loadChatHistory, refreshCredits]);

  // When CopilotChatPanel sends a message the server returns
  // the updated study pack — bubble it back so every other tab
  // re-renders. The chat panel itself owns its message list
  // (optimistic insertion + reconciliation with the server
  // response) so we don't reload history on every send.
  const onStudyPackUpdate = useCallback((pack) => {
    setStudyPack((prev) => ({
      ...(prev || {}),
      ...(pack || {}),
      // copilotMessages is the chat transcript; we keep our
      // local list in `chatMessages` for snappier UX, so we
      // don't blow it away here.
    }));
    if (pack?.copilotMessages) {
      setChatMessages(pack.copilotMessages);
    }
  }, []);

  const heroGradient =
    "linear-gradient(135deg, rgba(59,130,246,0.10) 0%, rgba(168,85,247,0.08) 40%, rgba(20,184,166,0.06) 100%)";

  const tabsToRender = useMemo(() => {
    // Audio-sourced packs don't have embedded images — hide the
    // Figures tab in that case to mirror mobile.
    if (material?.sourceType === "audio" || material?.source_type === "audio") {
      return TAB_DEFS.filter((t) => t.id !== "images");
    }
    return TAB_DEFS;
  }, [material]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={4} sx={{ maxWidth: 1400, mx: "auto", px: { xs: 0.5, md: 1 } }}>
        {/* Header card */}
        <Paper
          elevation={0}
          sx={(t) => ({
            position: "relative",
            borderRadius: 4,
            p: { xs: 2, md: 2.5 },
            mb: 2,
            border: "1px solid",
            borderColor: alpha(t.palette.primary.main, 0.18),
            backgroundImage: heroGradient,
            overflow: "hidden",
          })}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip arrow title="Back to Microlab Copilot">
                <IconButton onClick={() => navigate("/microlab-copilot")}>
                  <ArrowBackRoundedIcon />
                </IconButton>
              </Tooltip>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.3 }}>
                  <Chip
                    size="small"
                    label={(material?.sourceType || material?.source_type || "pdf").toUpperCase()}
                    color="info"
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                  {material?.creditsCost ? (
                    <Chip
                      size="small"
                      label={`${material.creditsCost} credits used`}
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                  ) : null}
                </Stack>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ letterSpacing: "-0.01em", lineHeight: 1.2 }}
                >
                  {studyPack?.lectureTitle || studyPack?.title || material?.title || "Study Pack"}
                </MDTypography>
              </Box>
            </Stack>
            <Box sx={{ minWidth: { md: 360 } }}>
              <CreditsBar credits={credits} compact />
            </Box>
          </Stack>
        </Paper>

        {/*
          Tab strip
          ─────────────────────────────────────────────────────
          The selected tab gets a modern indigo→violet→pink
          gradient pill background instead of the default thin
          underline indicator, which (a) reads as a proper
          "current section" affordance, (b) works equally well
          in light and dark mode (the pill itself is high
          contrast and overrides the tab text + icon colour),
          and (c) gives the strip more visual weight ("thicker
          tabs") without forcing every unselected tab to grow.

          The default MUI tab indicator (the bottom underline)
          is suppressed via TabIndicatorProps so it doesn't
          fight the pill. Tabs themselves get an extra few px
          of vertical padding for the chunkier look the user
          asked for.
        */}
        <Paper
          elevation={0}
          sx={(t) => ({
            borderRadius: 4,
            mb: 2,
            p: 0.75,
            border: "1px solid",
            borderColor: t.palette.divider,
            backgroundColor:
              t.palette.mode === "dark"
                ? alpha(t.palette.background.paper, 0.6)
                : t.palette.background.paper,
            backdropFilter: "saturate(180%) blur(8px)",
          })}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant={isSmall ? "scrollable" : "fullWidth"}
            scrollButtons={isSmall ? "auto" : false}
            allowScrollButtonsMobile
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={(t) => {
              const isDark = t.palette.mode === "dark";
              // Bright blue → deep navy: a tasteful gradient
              // that still feels "modern AI" without veering
              // into purple/pink territory the user wanted to
              // avoid. We use Tailwind blue-500 → blue-700 →
              // blue-900 stops; the diagonal sweep adds depth
              // without becoming a flat block of colour.
              const selectedGradient =
                "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 55%, #1e3a8a 100%)";
              // Anchor colour for hover wash + shadow tint.
              // Picked from the gradient's mid-stop so the
              // selected pill's shadow reads as "this colour,
              // emphasised" rather than two unrelated hues.
              const accent = "#1d4ed8";
              return {
                minHeight: 60,
                "& .MuiTab-root": {
                  minHeight: 60,
                  height: 60,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 14,
                  letterSpacing: "0.005em",
                  borderRadius: 2.5,
                  mx: 0.4,
                  color: isDark
                    ? alpha(t.palette.text.primary, 0.78)
                    : t.palette.text.secondary,
                  transition:
                    "background-color .2s ease, color .2s ease, box-shadow .2s ease, transform .15s ease",
                  "& .MuiSvgIcon-root": {
                    transition: "color .2s ease",
                  },
                  "&:hover": {
                    backgroundColor: alpha(accent, isDark ? 0.18 : 0.1),
                    color: t.palette.text.primary,
                  },
                },
                "& .Mui-selected": {
                  color: "#ffffff !important",
                  background: selectedGradient,
                  boxShadow: `0 10px 24px ${alpha(
                    accent,
                    isDark ? 0.5 : 0.38,
                  )}, 0 2px 6px ${alpha("#1e3a8a", 0.3)}`,
                  transform: "translateY(-1px)",
                  "& .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                  "&:hover": {
                    background: selectedGradient,
                    color: "#ffffff !important",
                  },
                },
                "& .MuiTabs-flexContainer": {
                  gap: 0.25,
                },
              };
            }}
          >
            {tabsToRender.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
              />
            ))}
          </Tabs>
        </Paper>

        {/* Tab body */}
        <Box sx={{ minHeight: 360 }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Loading study pack…
              </Typography>
            </Stack>
          ) : !studyPack ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3, borderStyle: "dashed" }}>
              <Typography variant="body2" color="text.secondary">
                This study pack isn't available. It may have been deleted or never finished processing.
              </Typography>
            </Paper>
          ) : (
            <>
              {activeTab === "summary" ? <SummaryTab studyPack={studyPack} /> : null}
              {activeTab === "structures" ? <KeyStructuresTab studyPack={studyPack} /> : null}
              {activeTab === "images" ? <ImagesTab studyPack={studyPack} /> : null}
              {activeTab === "quiz" ? <QuizTab studyPack={studyPack} /> : null}
              {activeTab === "flashcards" ? <FlashcardsTab studyPack={studyPack} /> : null}
              {activeTab === "chat" ? (
                <CopilotChatPanel
                  materialId={materialId}
                  initialMessages={chatMessages}
                  credits={credits}
                  onStudyPackUpdate={onStudyPackUpdate}
                  onCreditsChange={setCredits}
                  onInsufficientCredits={setInsufficient}
                  onNavigateToTab={setActiveTab}
                />
              ) : null}
              {activeTab === "topics" ? (
                <TopicsTab
                  materialId={materialId}
                  studyPack={studyPack}
                  credits={credits}
                  onStudyPackUpdate={onStudyPackUpdate}
                  onCreditsChange={setCredits}
                  onInsufficientCredits={setInsufficient}
                  onNavigateToTab={setActiveTab}
                />
              ) : null}
            </>
          )}
        </Box>
      </MDBox>

      <InsufficientCreditsModal
        open={!!insufficient}
        required={insufficient?.required}
        remaining={insufficient?.remaining}
        onClose={() => setInsufficient(null)}
      />

      <Footer />
    </DashboardLayout>
  );
}
